<template lang="pug">
.carousel(ref="el" :class="$attrs.class")
  .header.flex.justify-between.items-center
    slot(name="title")
    .flex.items-center(:class="paginationContainerClass")
      .page.whitespace-nowrap.flex-grow-0.mx-4.text-current.lg_block(
        v-if="numPages > 1 && !dotsOnly"
        :class="{ hidden: !paginationOnly }"
      ) {{ pageOffset + 1 }} / {{ numPages }}
      VPaginate.controls.flex-grow-0.lg_block(
        v-if="numPages > 1 && !dotsOnly"
        :class="{ hidden: !paginationOnly }"
        :min="0"
        :max="numPages - 1"
        :currentPage="pageOffset"
        :variant="variant"
        @change-page="goToPage($event)"
      )
  .carousel-slide-wrapper.overflow-x-auto.flex.flex-row.gap-4.pt-2(
    ref="slidesWrapper"
    @scroll="handleScroll"
    :class="{ 'full-bleed': fullBleed }"
  )
    slot(:currentPage="pageOffset")
  .flex.justify-center.mt-6(v-if="numPages > 1 && !paginationOnly" :class="[{ lg_hidden: !dotsOnly }, dotContainerClass]")
    .dot.w-2.h-2.mx-1.rounded-full.bg-gray-400.transition-colors.cursor-pointer(
      v-for="i in numPages"
      :key="i"
      :class="{ active: i - 1 === pageOffset }"
      @click="goToPage(i - 1)"
    )
    slot(name="next-dot" :goToNext="goToNext")
</template>

<script lang="ts" setup>
import debounce from '@/lib/debounce'
import type { VPaginateVariant } from '@/components/VPaginate.vue'
import type { PropType, Ref } from 'vue'

const props = defineProps({
  fullBleed: {
    type: Boolean,
    default: false,
  },
  autoScrollMs: {
    type: Number,
    default: 0,
  },
  paginationOnly: {
    type: Boolean,
    default: false,
  },
  dotsOnly: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String as PropType<VPaginateVariant>,
    default: 'light',
  },
  paginationContainerClass: {
    type: String,
    default: '',
  },
  dotContainerClass: {
    type: String,
    default: '',
  },
})

const slidesWrapper = ref() as Ref<HTMLElement | undefined>
const el = ref() as Ref<HTMLElement | undefined>
const slideWidth = ref(0)
const slideOffset = ref(0)
const numSlides = ref(0)
const slidesPerPage = ref(0)

const numPages = computed(() => {
  const num = Math.ceil(numSlides.value / slidesPerPage.value)
  return isFinite(num) ? num : 1
})

const pageOffset = computed(() => {
  let num = slideOffset.value / slidesPerPage.value
  if (slideOffset.value + slidesPerPage.value === numSlides.value) {
    num = Math.ceil(num)
  } else {
    num = Math.floor(num)
  }

  return isFinite(num) ? num : 0
})

const computeSlideMetaData = () => {
  const elWidth = el.value?.clientWidth
  const slides = el.value?.getElementsByClassName('carousel-slide')
  const sw = slidesWrapper.value
  if (!slides || !elWidth || !sw || slides.length < 2) {
    slideWidth.value = 0
    slideOffset.value = 0
    numSlides.value = slides?.length || 0
    return
  }

  const gapWidth = parseInt(getComputedStyle(sw).getPropertyValue('column-gap'))

  // Assume first slide is indicative of width
  const leftPos = (el: Element) => el.children[0].getBoundingClientRect().left
  slideWidth.value = leftPos(slides[1]) - leftPos(slides[0])
  numSlides.value = slides.length

  slidesPerPage.value = Math.floor((elWidth + gapWidth) / slideWidth.value) || 1
  slideOffset.value = Math.max(0, Math.min(numPages.value - 1, slideOffset.value))
  updateFrame()
}

const observer = new MutationObserver(computeSlideMetaData)

const updateFrame = () => {
  slidesWrapper.value?.scrollTo({ left: slideOffset.value * slideWidth.value, behavior: 'smooth' })
}

watch(
  slidesWrapper,
  () => {
    if (slidesWrapper.value) {
      observer.observe(slidesWrapper.value, { childList: true })
      // Depending on what's cached the browser back button will have it so
      // that the slides aren't loaded in time. This will make sure that
      // slides are all loaded
      // window.setTimeout(computeSlideMetaData, 1000)
      setTimeout(computeSlideMetaData, 1000)
    }
  },
  { immediate: true }
)

let autoScrollMsInterval: NodeJS.Timeout
onMounted(() => {
  window.addEventListener('resize', computeSlideMetaData)
  resetAutoScroll()
})

onUnmounted(() => {
  observer.disconnect()
  window.removeEventListener('resize', computeSlideMetaData)
  clearInterval(autoScrollMsInterval)
})

let debounceNext = false
const goToPage = (pageIndex: number) => {
  debounceNext = true
  slideOffset.value = pageIndex * slidesPerPage.value
  updateFrame()
  resetAutoScroll()
}

const goToNext = () => {
  goToPage(pageOffset.value < numSlides.value - 1 ? pageOffset.value + 1 : 0)
}

const updatePage = () => {
  const sw = slidesWrapper.value
  if (!sw) return

  const fractionalPage = sw.scrollLeft / slideWidth.value // might div 0
  if (!Number.isFinite(fractionalPage)) return

  // special case when at the end of the scroll
  // check precision within 1 because fractional pixels and precision errors are possible
  const isEndOfWrapper = Math.abs(sw.scrollLeft + sw.clientWidth - sw.scrollWidth) < 1

  if (isEndOfWrapper) {
    const max = numSlides.value - slidesPerPage.value
    slideOffset.value = Math.min(max, Math.ceil(fractionalPage))
  } else {
    slideOffset.value = Math.round(fractionalPage)
  }
}

// update page indicator when the scroll event STOPS firing
const updatePageButNotNow = debounce(() => {
  debounceNext = false
  updatePage()
}, 100)

// There's two scroll mechanisms, so we need two handlers.
// If the "next"/"prev" page buttons were pressed, we want to defer the
// scroll event until the animation has finished. (The animation itself
// triggers the scroll handler even though it's run programmatically.)
// If the user scrolls, we want to the handler to run immediately and often.
const handleScroll = () => {
  if (debounceNext) {
    updatePageButNotNow()
  } else {
    updatePage()
  }
}

function resetAutoScroll() {
  clearInterval(autoScrollMsInterval)

  if (props.autoScrollMs) {
    autoScrollMsInterval = setInterval(() => {
      goToNext()
    }, props.autoScrollMs)
  }
}
</script>

<style lang="stylus" scoped>
.dot.active
  @apply bg-gray-900

.controls .v-button
  @apply p-2

.carousel-slide-wrapper
  scroll-snap-type x mandatory
  // hide the scrollbar
  -ms-overflow-style none
  scrollbar-width none
  &::-webkit-scrollbar
    display none

  :deep(.carousel-slide)
    scroll-snap-align start

.carousel-slide-wrapper.full-bleed
  @apply w-screen transform -translate-x-1\/2
  margin-left 50%
  scroll-padding calc(50vw - (var(--screen-size) / 2) + 1.5rem)

  :deep(.carousel-slide)
    --slide-padding 0px
    --slide-width calc((var(--screen-size) - 2rem - var(--slides-per-page) * 1rem) / var(--slides-per-page) + var(--slide-padding))
    flex-basis var(--slide-width)
    &:first-child, &:last-child
      --slide-padding calc(50vw - (var(--screen-size) / 2) + 1.5rem)
    &:first-child
      padding-left var(--slide-padding)
    &:last-child
      padding-right var(--slide-padding)

@screen lg // desktop view - no full bleed
  .carousel-slide-wrapper.full-bleed
    @apply w-full transform-none translate-x-0
    margin-left 0
    scroll-padding 0

    :deep(.carousel-slide)
      &:first-child, &:last-child
        --slide-padding 0px
</style>
